import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Card, Table, Typography, Alert, Select, Space, Row, Col, Button, Radio, Skeleton, Statistic } from 'antd';
import { SwapOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import ExpenseService, { LocationExpense, ExpenseItem, groupExpensesByTier } from '../../services/ExpenseService';
import DashboardService, { DateRange } from '../../services/DashboardService';
import { formatDisplayDate } from '../../utils/formatters';
import WeekSelectorModal from '../../components/dashboard/WeekSelectorModal';
import { SkeletonLoader } from '../../components/dashboard/SkeletonLoader';
import { useAuth } from '../../contexts/AuthContext';
import { NewItemService } from '../../services/NewItemService';
import {
  getTierForLocation,
  groupItemsByCriticalItem,
  calculateCriticalItemAvgQty,
  getSalesForLocation as getSalesForLocationUtil,
  CRITICAL_ITEMS_MAPPING,
  CriticalItem,
} from './utils';
import { ExpenseMobileCard } from './components/ExpenseMobileCard';
import { CompareLocationsModal } from './components/CompareLocationsModal';
import { LocationComparisonView } from './components/LocationComparisonView';

const { Title, Text } = Typography;

const Expenses: React.FC = () => {
  const { user } = useAuth();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expenseData, setExpenseData] = useState<LocationExpense[]>([]);
  const [salesByLocation, setSalesByLocation] = useState<Record<string, number>>({});
  const [viewMode, setViewMode] = useState<'weekly' | 'quarterly'>('weekly');
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [dateRanges, setDateRanges] = useState<DateRange[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(null);
  const [availableQuarters, setAvailableQuarters] = useState<string[]>([]);
  const [selectedQuarter, setSelectedQuarter] = useState<{ quarter: string; year: number } | null>(null);
  const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false);
  const [itemViewMode, setItemViewMode] = useState<Record<string, 'all' | 'critical'>>({});
  const [newItems, setNewItems] = useState<string[]>([]);
  const [acknowledgingItems, setAcknowledgingItems] = useState(false);
  const [item8WeekAverages, setItem8WeekAverages] = useState<Record<string, Record<string, { averageQuantity: number; averageExpense: number }>>>({});
  const [item4WeekAverages, setItem4WeekAverages] = useState<Record<string, Record<string, { averageQuantity: number; averageExpense: number }>>>({});
  const [loadingAverages, setLoadingAverages] = useState<Record<string, boolean>>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<Record<string, string[]>>({});
  const tierCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const newItemService = useMemo(() => new NewItemService(), []);
  const navigationProcessedRef = useRef<string | null>(null); // Track processed navigation to prevent re-processing

  // Compare mode state
  const [compareMode, setCompareMode] = useState(false);
  const [comparedLocations, setComparedLocations] = useState<[string, string] | null>(null);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

  // Calculate tier groups for organizing data by sales tier
  const tierGroups = useMemo(() => {
    return groupExpensesByTier(expenseData, viewMode === 'quarterly');
  }, [expenseData, viewMode]);

  // Check if user should see new items alert
  const shouldShowNewItems = user?.email === 'moe@pitaway.com';

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Function to fetch new items
  const fetchNewItems = useCallback(async () => {
    if (shouldShowNewItems) {
      try {
        const items = await newItemService.getPendingNewItems();
        setNewItems(items);
      } catch (error) {
        console.error('Failed to fetch new items:', error);
      }
    }
  }, [shouldShowNewItems, newItemService]);

  // Fetch new items on mount and when user changes
  useEffect(() => {
    fetchNewItems();
  }, [fetchNewItems]);

  // Refresh new items when tab becomes visible (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && shouldShowNewItems) {
        fetchNewItems();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [shouldShowNewItems, fetchNewItems]);

  const handleAcknowledgeNewItems = async () => {
    if (newItems.length === 0) return;
    
    setAcknowledgingItems(true);
    try {
      await newItemService.acknowledgeNewItems(newItems);
      // Refresh the list after acknowledging (in case another user acknowledged items)
      await fetchNewItems();
    } catch (error) {
      console.error('Failed to acknowledge new items:', error);
      setError('Failed to acknowledge new items. Please try again.');
    } finally {
      setAcknowledgingItems(false);
    }
  };

  const fetchQuarters = React.useCallback(async () => {
    try {
      const quarterYears = await DashboardService.getAvailableQuarters();
      setAvailableQuarters(quarterYears);
      if (quarterYears.length > 0 && !selectedQuarter) {
        // Default to most recent quarter-year (first in list, already sorted)
        // Backend returns format "Q2 2025" (space-separated), not "Q2-2025"
        const quarterStr = quarterYears[0].trim();
        const match = quarterStr.match(/^(Q[1-4])\s+(\d{4})$/i);
        if (match) {
          const [, quarter, yearStr] = match;
          const year = parseInt(yearStr, 10);
          if (!isNaN(year)) {
            setSelectedQuarter({ quarter, year });
          }
        } else {
          // Fallback: try splitting by space or dash
          const parts = quarterStr.split(/\s+|-/);
          if (parts.length >= 2) {
            const quarter = parts[0];
            const yearStr = parts[parts.length - 1];
            const year = parseInt(yearStr, 10);
            if (!isNaN(year)) {
              setSelectedQuarter({ quarter, year });
            }
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch available quarters');
    }
  }, [selectedQuarter]);

  const fetchExpenseWeeks = useCallback(async () => {
    try {
      const ranges = await ExpenseService.getAvailableExpenseWeeks();
      setDateRanges(ranges);
      if (ranges.length > 0 && !selectedDateRange) {
        setSelectedDateRange(ranges[0]);
      } else if (ranges.length === 0) {
        setSelectedDateRange(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch available weeks');
    }
  }, [selectedDateRange]);

  useEffect(() => {
    if (viewMode === 'weekly') {
      setLoading(true); // Show loading immediately when switching to weekly
      fetchExpenseWeeks();
    } else {
      fetchQuarters();
    }
  }, [viewMode, fetchExpenseWeeks, fetchQuarters]);

  const fetchExpenseData = React.useCallback(async () => {
    if (viewMode === 'weekly' && !selectedDateRange) {
      // Don't set loading here - it's already set by the useEffect that fetches weeks
      // Just return and wait for selectedDateRange to be set
      return;
    }
    if (viewMode === 'quarterly' && !selectedQuarter) {
      // Don't set loading here - it's already set by the useEffect that fetches quarters
      // Just return and wait for selectedQuarter to be set
      return;
    }

    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Use a delay before showing loading to prevent flash when cached data returns instantly
    // Weekly makes 2 API calls, so we need a bit more time for both to resolve from cache
    loadingTimeoutRef.current = setTimeout(() => {
      setLoading(true);
    }, 150); // 150ms delay - enough for 2 cached calls to return
    
    setError(null);
    try {
      let data: LocationExpense[] = [];
      let startDate: string | undefined;
      let endDate: string | undefined;

      if (viewMode === 'weekly' && selectedDateRange) {
        startDate = selectedDateRange.startDate;
        endDate = selectedDateRange.endDate;
        console.log('Fetching weekly expense data for:', startDate, 'to', endDate);
        
        // Make both API calls in parallel for weekly (faster when cached)
        const [expenseDataResult, salesDataResult] = await Promise.allSettled([
          ExpenseService.getExpenseData(startDate, endDate),
          ExpenseService.getToastSalesByLocation(startDate, endDate)
        ]);
        
        if (expenseDataResult.status === 'fulfilled') {
          data = expenseDataResult.value;
          console.log('Received expense data:', data.length, 'locations');
        } else {
          console.error('Failed to fetch expense data:', expenseDataResult.reason);
          throw expenseDataResult.reason;
        }
        
        if (salesDataResult.status === 'fulfilled') {
          console.log('Fetched weekly Toast sales from files:', salesDataResult.value);
          setSalesByLocation(salesDataResult.value);
        } else {
          console.warn('Failed to fetch weekly Toast sales data:', salesDataResult.reason);
          setSalesByLocation({}); // Continue without sales data
        }
      } else if (viewMode === 'quarterly' && selectedQuarter) {
        // Calculate quarter date range matching backend logic
        const quarterNum = parseInt(selectedQuarter.quarter.substring(1));
        switch (quarterNum) {
          case 1:
            startDate = `${selectedQuarter.year}-01-01`;
            endDate = `${selectedQuarter.year}-03-31`;
            break;
          case 2:
            startDate = `${selectedQuarter.year}-04-01`;
            endDate = `${selectedQuarter.year}-06-30`;
            break;
          case 3:
            startDate = `${selectedQuarter.year}-07-01`;
            endDate = `${selectedQuarter.year}-09-30`;
            break;
          case 4:
            startDate = `${selectedQuarter.year}-10-01`;
            endDate = `${selectedQuarter.year}-12-31`;
            break;
          default:
            startDate = undefined;
            endDate = undefined;
        }
        
        // Make both API calls in parallel for quarterly (faster when cached)
        const [expenseDataResult, salesDataResult] = await Promise.allSettled([
          ExpenseService.getQuarterlyExpenseData(selectedQuarter.quarter, selectedQuarter.year),
          ExpenseService.getQuarterlyToastSalesByLocation(selectedQuarter.quarter, selectedQuarter.year)
        ]);
        
        if (expenseDataResult.status === 'fulfilled') {
          data = expenseDataResult.value;
        } else {
          console.error('Failed to fetch quarterly expense data:', expenseDataResult.reason);
          throw expenseDataResult.reason;
        }
        
        if (salesDataResult.status === 'fulfilled') {
          console.log('Fetched quarterly Toast sales from files:', salesDataResult.value);
          setSalesByLocation(salesDataResult.value);
        } else {
          console.warn('Failed to fetch quarterly Toast sales data:', salesDataResult.reason);
          setSalesByLocation({}); // Continue without sales data
        }
      } else {
        setSalesByLocation({});
      }

      setExpenseData(data);
      if (data.length === 0 && viewMode === 'weekly') {
        console.warn('No expense data returned for weekly view. Check backend logs for details.');
      }
    } catch (err: any) {
      console.error('Error fetching expense data:', err);
      setError(err.message || 'Failed to fetch expense data');
    } finally {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      setLoading(false);
      
      // Refresh new items after expense data is loaded (new items might have been detected during file processing)
      if (shouldShowNewItems) {
        // Small delay to ensure backend has finished processing
        setTimeout(() => {
          fetchNewItems();
        }, 500);
      }
    }
  }, [viewMode, selectedQuarter, selectedDateRange, shouldShowNewItems, fetchNewItems]);

  useEffect(() => {
    fetchExpenseData();
  }, [fetchExpenseData]);

  // Handle navigation from dashboard to open specific location
  useEffect(() => {
    const navigationState = routerLocation.state as { locationToOpen?: string; dateRange?: DateRange } | null;
    
    // Create a unique key for this navigation to prevent re-processing
    const navigationKey = navigationState?.locationToOpen && navigationState?.dateRange
      ? `${navigationState.locationToOpen}-${navigationState.dateRange.startDate}-${navigationState.dateRange.endDate}`
      : null;
    
    // Skip if no navigation state
    if (!navigationKey) {
      return;
    }
    
    // Skip if we've already processed this exact navigation
    if (navigationProcessedRef.current === navigationKey) {
      return;
    }
    
    if (navigationState?.locationToOpen && navigationState?.dateRange) {
      // Set the date range if provided (only if not already set to avoid unnecessary re-fetches)
      if (viewMode === 'weekly' && navigationState.dateRange) {
        // Only set if it's different from current to avoid unnecessary updates
        if (!selectedDateRange || 
            selectedDateRange.startDate !== navigationState.dateRange.startDate ||
            selectedDateRange.endDate !== navigationState.dateRange.endDate) {
          setSelectedDateRange(navigationState.dateRange);
          // Don't mark as processed yet - wait for data to load
          return; // Exit early, let the date range change trigger data fetch
        }
      }
      
      // Wait for expense data to load and finish loading, then open the location
      if (expenseData.length > 0 && !loading) {
        const locationToOpen = navigationState.locationToOpen;
        const locationRecord = expenseData.find(loc => loc.location === locationToOpen);
        if (locationRecord) {
          // Mark as processed BEFORE clearing state to prevent race conditions
          navigationProcessedRef.current = navigationKey;
          
          // Clear navigation state immediately to prevent re-triggering on subsequent data changes
          navigate(routerLocation.pathname, { replace: true, state: null });
          
          // Find which tier this location belongs to
          const tier = getTierForLocation(locationRecord.totalSales ?? 0, viewMode === 'quarterly');
          
          // Expand the row for this location
          setExpandedRowKeys(prev => ({
            ...prev,
            [tier]: [...(prev[tier] || []).filter(k => k !== locationToOpen), locationToOpen]
          }));
          
          // Scroll to the tier card - use longer timeout to ensure DOM is ready after expansion
          setTimeout(() => {
            const tierCard = tierCardRefs.current[tier];
            if (tierCard) {
              tierCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 300);
        }
      }
    }
  }, [routerLocation.state, routerLocation.pathname, expenseData, viewMode, loading, selectedDateRange, navigate]);


  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatQuarterLabel = (quarter: string, year: number) => {
    return `${quarter.toUpperCase()} ${year}`;
  };

  // Fetch 4-week and 8-week averages when location is expanded (weekly view only)
  const handleLocationExpand = useCallback(async (expanded: boolean, record: LocationExpense) => {
    if (expanded && viewMode === 'weekly' && selectedDateRange) {
      // Check if we already have averages for this location
      const need8Week = !item8WeekAverages[record.location];
      const need4Week = !item4WeekAverages[record.location];

      if ((need8Week || need4Week) && !loadingAverages[record.location]) {
        setLoadingAverages(prev => ({ ...prev, [record.location]: true }));
        try {
          // Fetch both in parallel
          const [avg8Week, avg4Week] = await Promise.all([
            need8Week ? ExpenseService.getItem8WeekAverages(record.location, selectedDateRange.endDate) : Promise.resolve(null),
            need4Week ? ExpenseService.getItem4WeekAverages(record.location, selectedDateRange.endDate) : Promise.resolve(null),
          ]);
          if (avg8Week) {
            setItem8WeekAverages(prev => ({ ...prev, [record.location]: avg8Week }));
          }
          if (avg4Week) {
            setItem4WeekAverages(prev => ({ ...prev, [record.location]: avg4Week }));
          }
        } catch (error) {
          console.error('Failed to fetch averages:', error);
        } finally {
          setLoadingAverages(prev => ({ ...prev, [record.location]: false }));
        }
      }
    }
  }, [viewMode, selectedDateRange, item8WeekAverages, item4WeekAverages, loadingAverages]);

  // Create expandable row render function for desktop view
  const expandedRowRender = (record: LocationExpense) => {
    const itemViewModeForLocation = itemViewMode[record.location] || 'critical';
    const averages8Week = item8WeekAverages[record.location] || {};
    const averages4Week = item4WeekAverages[record.location] || {};
    const isLoadingAvg = loadingAverages[record.location];

    // Calculate date ranges for display in column headers
    let period4Week = '';
    let period8Week = '';
    if (selectedDateRange) {
      // Parse date manually to avoid timezone issues (new Date("YYYY-MM-DD") parses as UTC)
      const [year, month, day] = selectedDateRange.endDate.split('-').map(Number);
      const endDate = new Date(year, month - 1, day); // Sunday
      const formatShortDate = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear().toString().slice(-2)}`;

      // Get Monday of current week (endDate is Sunday, so go back 6 days)
      const currentMonday = new Date(endDate);
      currentMonday.setDate(currentMonday.getDate() - 6);

      // 4-week: Monday 3 weeks before current Monday to Sunday
      const start4Week = new Date(currentMonday);
      start4Week.setDate(start4Week.getDate() - 21); // 3 weeks back
      period4Week = `${formatShortDate(start4Week)} - ${formatShortDate(endDate)}`;

      // 8-week: Monday 7 weeks before current Monday to Sunday
      const start8Week = new Date(currentMonday);
      start8Week.setDate(start8Week.getDate() - 49); // 7 weeks back
      period8Week = `${formatShortDate(start8Week)} - ${formatShortDate(endDate)}`;
    }
    
    const itemColumns = [
      {
        title: 'Item Name',
        dataIndex: 'itemName',
        key: 'itemName',
        width: 300,
      },
      {
        title: 'Confirmed Qty',
        dataIndex: 'billedQuantity',
        key: 'billedQuantity',
        width: 120,
        align: 'right' as const,
        render: (qty: number) => Math.round(qty).toLocaleString(),
        sorter: (a: ExpenseItem, b: ExpenseItem) => a.billedQuantity - b.billedQuantity,
      },
      {
        title: 'Amount',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 130,
        align: 'right' as const,
        render: (amount: number, item: ExpenseItem) => (
          <span style={{
            color: item.itemExpenseLevel === 'HIGH' ? '#ff4d4f' : undefined,
            fontWeight: item.itemExpenseLevel === 'HIGH' ? 600 : 400
          }}>
            {formatCurrency(amount)}
          </span>
        ),
        sorter: (a: ExpenseItem, b: ExpenseItem) => a.totalAmount - b.totalAmount,
        defaultSortOrder: 'descend' as const,
      },
      {
        title: '% of Total',
        key: 'percentageOfLocationTotal',
        width: 100,
        align: 'right' as const,
        render: (_: any, item: ExpenseItem) => {
          const percentage = item.percentageOfLocationTotal;
          if (percentage === undefined) return '-';
          return (
            <span style={{
              color: item.itemExpenseLevel === 'HIGH' ? '#ff4d4f' : undefined,
              fontWeight: item.itemExpenseLevel === 'HIGH' ? 600 : 400
            }}>
              {percentage.toFixed(2)}%
            </span>
          );
        },
        sorter: (a: ExpenseItem, b: ExpenseItem) =>
          (a.percentageOfLocationTotal ?? 0) - (b.percentageOfLocationTotal ?? 0),
      },
    ];

    const criticalItemColumns = [
      {
        title: 'Critical Item Name',
        dataIndex: 'criticalItemName',
        key: 'criticalItemName',
        width: 300,
      },
      {
        title: 'Total Quantity',
        dataIndex: 'totalQuantity',
        key: 'totalQuantity',
        width: 150,
        align: 'right' as const,
        render: (qty: number) => Math.round(qty).toLocaleString(),
        sorter: (a: CriticalItem, b: CriticalItem) => a.totalQuantity - b.totalQuantity,
      },
      {
        title: 'Total Amount',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 150,
        align: 'right' as const,
        render: (amount: number) => formatCurrency(amount),
        sorter: (a: CriticalItem, b: CriticalItem) => a.totalAmount - b.totalAmount,
      },
      ...(viewMode === 'weekly' && selectedDateRange ? [
        {
          title: (
            <div style={{ textAlign: 'right' }}>
              <div>4-Wk Avg Wkly Qty</div>
              <div style={{ fontSize: 10, color: '#888', fontWeight: 'normal' }}>{period4Week}</div>
            </div>
          ),
          key: '4weekAvgQty',
          width: 140,
          align: 'right' as const,
          render: (_: any, criticalItem: CriticalItem) => {
            if (isLoadingAvg) {
              return <Skeleton.Input active size="small" style={{ width: 60 }} />;
            }
            const matchingItems = record.items.filter(item => {
              const criticalItemName = CRITICAL_ITEMS_MAPPING[item.itemName] || 'Other';
              return criticalItemName === criticalItem.criticalItemName;
            });

            if (matchingItems.length === 0) return '-';

            let totalAvgQty = 0;
            let foundAny = false;
            matchingItems.forEach(item => {
              const avg = averages4Week[item.itemName];
              if (avg) {
                totalAvgQty += avg.averageQuantity;
                foundAny = true;
              }
            });

            if (!foundAny) return '-';
            return Math.round(totalAvgQty).toLocaleString();
          },
          sorter: (a: CriticalItem, b: CriticalItem) => {
            const avgA = calculateCriticalItemAvgQty(a, record.items, averages4Week);
            const avgB = calculateCriticalItemAvgQty(b, record.items, averages4Week);
            return avgA - avgB;
          },
        },
        {
          title: (
            <div style={{ textAlign: 'right' }}>
              <div>8-Wk Avg Wkly Qty</div>
              <div style={{ fontSize: 10, color: '#888', fontWeight: 'normal' }}>{period8Week}</div>
            </div>
          ),
          key: '8weekAvgQty',
          width: 140,
          align: 'right' as const,
          render: (_: any, criticalItem: CriticalItem) => {
            if (isLoadingAvg) {
              return <Skeleton.Input active size="small" style={{ width: 60 }} />;
            }
            const matchingItems = record.items.filter(item => {
              const criticalItemName = CRITICAL_ITEMS_MAPPING[item.itemName] || 'Other';
              return criticalItemName === criticalItem.criticalItemName;
            });

            if (matchingItems.length === 0) return '-';

            let totalAvgQty = 0;
            let foundAny = false;
            matchingItems.forEach(item => {
              const avg = averages8Week[item.itemName];
              if (avg) {
                totalAvgQty += avg.averageQuantity;
                foundAny = true;
              }
            });

            if (!foundAny) return '-';
            return Math.round(totalAvgQty).toLocaleString();
          },
          sorter: (a: CriticalItem, b: CriticalItem) => {
            const avgA = calculateCriticalItemAvgQty(a, record.items, averages8Week);
            const avgB = calculateCriticalItemAvgQty(b, record.items, averages8Week);
            return avgA - avgB;
          },
        },
      ] : []),
    ];

    const criticalItems = groupItemsByCriticalItem(record.items);

    return (
      <div style={{ padding: '16px 0' }}>
        <div style={{ marginBottom: 16 }}>
          <Radio.Group
            value={itemViewModeForLocation}
            onChange={(e) => setItemViewMode({ ...itemViewMode, [record.location]: e.target.value })}
            buttonStyle="solid"
          >
            <Radio.Button value="critical">Critical Items</Radio.Button>
            <Radio.Button value="all">All Items</Radio.Button>
          </Radio.Group>
        </div>
        {itemViewModeForLocation === 'all' ? (
          <Table
            columns={itemColumns}
            dataSource={record.items}
            pagination={false}
            rowKey={(item, index) => `${item.itemName}-${index}`}
            size="small"
          />
        ) : (
          <Table
            columns={criticalItemColumns}
            dataSource={criticalItems}
            pagination={false}
            rowKey={(item) => item.criticalItemName}
            size="small"
          />
        )}
      </div>
    );
  };

  // Helper function to find sales for a location (case-insensitive and trimmed matching)
  const getSalesForLocation = useCallback((locationName: string): number => {
    return getSalesForLocationUtil(locationName, salesByLocation);
  }, [salesByLocation]);

  const columns = [
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      fixed: 'left' as const,
      width: 200,
    },
    {
      title: 'DM',
      dataIndex: 'dm',
      key: 'dm',
      width: 150,
    },
    {
      title: viewMode === 'weekly' ? 'Prior Wk GFS' : 'Qtr GFS',
      dataIndex: 'totalExpense',
      key: 'totalExpense',
      width: 150,
      align: 'right' as const,
      render: (amount: number) => formatCurrency(amount),
      sorter: (a: LocationExpense, b: LocationExpense) => a.totalExpense - b.totalExpense,
      defaultSortOrder: 'descend' as const,
    },
    {
      title: viewMode === 'weekly' ? 'Prior Wk Sales' : 'Qtr Sales',
      dataIndex: 'totalSales',
      key: 'totalSales',
      width: 150,
      align: 'right' as const,
      render: (sales: number | undefined) => {
        // Use totalSales directly from expense data (same pattern as Dashboard)
        if (sales === undefined || sales === null || sales === 0) return '--';
        return formatCurrency(sales);
      },
      sorter: (a: LocationExpense, b: LocationExpense) => (a.totalSales ?? 0) - (b.totalSales ?? 0),
    },
    {
      title: 'GFS %',
      key: 'expensePercentageOfSales',
      width: 150,
      align: 'right' as const,
      render: (_: any, record: LocationExpense) => {
        const percentage = record.expensePercentageOfSales;
        if (percentage === undefined || percentage === 0) return '-';
        return `${percentage.toFixed(2)}%`;
      },
      sorter: (a: LocationExpense, b: LocationExpense) =>
        (a.expensePercentageOfSales ?? 0) - (b.expensePercentageOfSales ?? 0),
    },
    {
      title: 'Item Count',
      key: 'itemCount',
      width: 120,
      align: 'right' as const,
      render: (_: any, record: LocationExpense) => Math.round(record.itemCount ?? record.items.length).toLocaleString(),
      sorter: (a: LocationExpense, b: LocationExpense) => (a.itemCount ?? 0) - (b.itemCount ?? 0),
    },
  ];

  const quarterOptions = availableQuarters.map(q => {
    // Backend returns format "Q2 2025" (space-separated), not "Q2-2025"
    const quarterStr = q.trim();
    const match = quarterStr.match(/^(Q[1-4])\s+(\d{4})$/i);
    if (match) {
      const [, quarter, yearStr] = match;
      const year = parseInt(yearStr, 10);
      if (!isNaN(year)) {
        return {
          label: formatQuarterLabel(quarter, year),
          value: q,
        };
      }
    }
    // Fallback: try splitting by space or dash
    const parts = quarterStr.split(/\s+|-/);
    if (parts.length >= 2) {
      const quarter = parts[0];
      const yearStr = parts[parts.length - 1];
      const year = parseInt(yearStr, 10);
      if (!isNaN(year)) {
        return {
          label: formatQuarterLabel(quarter, year),
          value: q,
        };
      }
    }
    // If parsing fails, return as-is
    return {
      label: q,
      value: q,
    };
  });

  const handleQuarterChange = (value: string) => {
    // Backend returns format "Q2 2025" (space-separated), not "Q2-2025"
    const quarterStr = value.trim();
    const match = quarterStr.match(/^(Q[1-4])\s+(\d{4})$/i);
    if (match) {
      const [, quarter, yearStr] = match;
      const year = parseInt(yearStr, 10);
      if (!isNaN(year)) {
        setSelectedQuarter({ quarter, year });
        // Reset navigation processed ref when user manually changes quarter
        navigationProcessedRef.current = null;
        return;
      }
    }
    // Fallback: try splitting by space or dash
    const parts = quarterStr.split(/\s+|-/);
    if (parts.length >= 2) {
      const quarter = parts[0];
      const yearStr = parts[parts.length - 1];
      const year = parseInt(yearStr, 10);
      if (!isNaN(year)) {
        setSelectedQuarter({ quarter, year });
        // Reset navigation processed ref when user manually changes quarter
        navigationProcessedRef.current = null;
        return;
      }
    }
    // If parsing fails, log error but don't crash
    console.error('Failed to parse quarter string:', value);
  };

  const handleViewModeChange = (value: 'weekly' | 'quarterly') => {
    setViewMode(value);
    setExpenseData([]);
    setSalesByLocation({});
    setItemViewMode({}); // Reset item view mode when switching between quarterly and weekly
    // Reset navigation processed ref when view mode changes
    navigationProcessedRef.current = null;
    // Don't set loading here - let fetchExpenseData handle it
    // This prevents loading flash when cached data returns instantly
  };

  const handleWeekSelect = (range: DateRange) => {
    setSelectedDateRange(range);
    setIsWeekSelectorOpen(false);
    // Reset navigation processed ref when user manually changes date range
    navigationProcessedRef.current = null;
  };

  // Always use date range format, never the label (which might have "2025/__" format)
  const selectedWeekLabel = selectedDateRange
    ? `${formatDisplayDate(selectedDateRange.startDate)} → ${formatDisplayDate(selectedDateRange.endDate)}`
    : 'Select reporting week';

  const handleWeekSelectorOpen = () => setIsWeekSelectorOpen(true);
  const handleWeekSelectorClose = () => setIsWeekSelectorOpen(false);

  const emptyStateDescription =
    viewMode === 'weekly'
      ? 'No GFS expense data found for the selected week. Upload the corresponding weekly GFS file and try again.'
      : 'No GFS expense data found for the selected quarter. The GFS quarterly file may not contain data for this quarter.';

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {shouldShowNewItems && newItems.length > 0 && (
          <Alert
            message="New Items Detected"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  {newItems.length} new item{newItems.length > 1 ? 's' : ''} detected in GFS files:
                </p>
                <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                  {newItems.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <Button
                  type="primary"
                  onClick={handleAcknowledgeNewItems}
                  loading={acknowledgingItems}
                >
                  Mark as Known & Add to List
                </Button>
              </div>
            }
            type="warning"
            showIcon
            closable
            onClose={() => setNewItems([])}
          />
        )}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 12 : 16,
        }}>
          <Title level={isMobile ? 4 : 2} style={{ margin: 0, fontSize: isMobile ? 18 : undefined }}>
            GFS Expenses
          </Title>
          <Space size={8} wrap style={{ width: isMobile ? '100%' : 'auto' }}>
            <div data-tour="compare-section">
              <Button
                icon={<SwapOutlined />}
                onClick={() => setIsCompareModalOpen(true)}
                disabled={expenseData.length < 2 || loading}
              >
                Compare
              </Button>
            </div>
            <div data-tour="expenses-filters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
              <Select
                value={viewMode}
                onChange={handleViewModeChange}
                style={{ width: isMobile ? '100%' : 120, minWidth: isMobile ? undefined : 120 }}
                options={[
                  { label: 'Weekly', value: 'weekly' },
                  { label: 'Quarterly', value: 'quarterly' },
                ]}
              />
              {viewMode === 'weekly' ? (
              <WeekSelectorModal
                open={isWeekSelectorOpen}
                dateRanges={dateRanges}
                selectedRange={selectedDateRange}
                onSelect={handleWeekSelect}
                onClose={handleWeekSelectorClose}
                formatDisplayDate={formatDisplayDate}
              >
                <Button
                  onClick={handleWeekSelectorOpen}
                  disabled={dateRanges.length === 0 || loading}
                  style={{
                    width: isMobile ? '100%' : 'auto',
                    minWidth: isMobile ? undefined : 260,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                    {selectedWeekLabel}
                  </span>
                </Button>
              </WeekSelectorModal>
            ) : (
              <Select
                value={selectedQuarter && !isNaN(selectedQuarter.year)
                  ? `${selectedQuarter.quarter} ${selectedQuarter.year}`
                  : undefined}
                onChange={handleQuarterChange}
                style={{ width: isMobile ? '100%' : 200, minWidth: isMobile ? undefined : 200 }}
                options={quarterOptions}
                loading={availableQuarters.length === 0}
                placeholder="Select Quarter"
                showSearch
                optionFilterProp="label"
              />
              )}
            </div>
          </Space>
        </div>

        {error && <Alert message="Error" description={error} type="error" showIcon />}

        {loading || (viewMode === 'weekly' && !selectedDateRange) || (viewMode === 'quarterly' && !selectedQuarter) ? (
          <Card>
            <SkeletonLoader tierCount={1} isMobile={isMobile} columns={columns} />
          </Card>
        ) : expenseData.length === 0 ? (
          <Card>
            <Alert
              message="No Expense Data Available"
              description={emptyStateDescription}
              type="info"
              showIcon
            />
          </Card>
        ) : compareMode && comparedLocations ? (
          <LocationComparisonView
            locationA={expenseData.find(loc => loc.location === comparedLocations[0])!}
            locationB={expenseData.find(loc => loc.location === comparedLocations[1])!}
            viewMode={viewMode}
            onExit={() => {
              setCompareMode(false);
              setComparedLocations(null);
            }}
            item8WeekAveragesA={item8WeekAverages[comparedLocations[0]]}
            item8WeekAveragesB={item8WeekAverages[comparedLocations[1]]}
            item4WeekAveragesA={item4WeekAverages[comparedLocations[0]]}
            item4WeekAveragesB={item4WeekAverages[comparedLocations[1]]}
            loadingAverages={loadingAverages[comparedLocations[0]] || loadingAverages[comparedLocations[1]]}
            onLoadAverages={(location) => {
              if (selectedDateRange && !item8WeekAverages[location] && !loadingAverages[location]) {
                setLoadingAverages(prev => ({ ...prev, [location]: true }));
                Promise.all([
                  ExpenseService.getItem8WeekAverages(location, selectedDateRange.endDate),
                  ExpenseService.getItem4WeekAverages(location, selectedDateRange.endDate),
                ]).then(([avg8Week, avg4Week]) => {
                  setItem8WeekAverages(prev => ({ ...prev, [location]: avg8Week }));
                  setItem4WeekAverages(prev => ({ ...prev, [location]: avg4Week }));
                }).catch(console.error).finally(() => {
                  setLoadingAverages(prev => ({ ...prev, [location]: false }));
                });
              }
            }}
            isMobile={isMobile}
          />
        ) : (
          <>
            {/* Summary Metrics */}
            {(() => {
              // Use totalSales directly from expense data (backend now correctly returns eighthWeekSales for weekly)
              const totalSalesSum = expenseData.reduce((sum, loc) => sum + (loc.totalSales ?? 0), 0);
              const totalExpenseSum = expenseData.reduce((sum, loc) => sum + loc.totalExpense, 0);
              const overallExpensePct = totalSalesSum > 0 ? (totalExpenseSum / totalSalesSum) * 100 : 0;

              // Tier breakdown data
              const tierBreakdown = tierGroups.filter(g => g.locations.length > 0).map(tierGroup => {
                const tierSales = tierGroup.locations.reduce((sum, loc) => sum + (loc.totalSales ?? 0), 0);
                const tierExpenses = tierGroup.locations.reduce((sum, loc) => sum + loc.totalExpense, 0);
                const tierExpensePct = tierSales > 0 ? (tierExpenses / tierSales) * 100 : 0;
                return {
                  key: tierGroup.tier,
                  tier: tierGroup.tierLabel,
                  description: tierGroup.tierDescription,
                  locations: tierGroup.locations.length,
                  totalSales: tierSales,
                  totalExpenses: tierExpenses,
                  expensePct: tierExpensePct,
                };
              });

              return (
                <Card size="small" style={{ marginBottom: 16 }}>
                  {/* Top Summary Row */}
                  <Row gutter={[24, 16]} style={{ marginBottom: 20 }}>
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title={viewMode === 'weekly' ? 'Prior Wk Sales' : 'Qtr Total Sales'}
                        value={totalSalesSum}
                        precision={0}
                        prefix="$"
                        valueStyle={{ fontSize: isMobile ? 16 : 18 }}
                        formatter={(value) => Number(value).toLocaleString()}
                      />
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title={viewMode === 'weekly' ? 'Prior Wk GFS' : 'Qtr Total GFS'}
                        value={totalExpenseSum}
                        precision={0}
                        prefix="$"
                        valueStyle={{ fontSize: isMobile ? 16 : 18 }}
                        formatter={(value) => Number(value).toLocaleString()}
                      />
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title="Overall GFS %"
                        value={overallExpensePct}
                        precision={1}
                        suffix="%"
                        valueStyle={{ fontSize: isMobile ? 16 : 18 }}
                      />
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title="Locations"
                        value={expenseData.length}
                        valueStyle={{ fontSize: isMobile ? 16 : 18 }}
                      />
                    </Col>
                  </Row>

                  {/* Tier Breakdown */}
                  <Text strong style={{ display: 'block', marginBottom: 12 }}>By Tier Breakdown</Text>
                  {isMobile ? (
                    // Mobile: Card-based tier breakdown
                    <div>
                      {tierBreakdown.map((tier) => (
                        <div
                          key={tier.key}
                          style={{
                            padding: '12px',
                            marginBottom: 8,
                            backgroundColor: '#fafafa',
                            borderRadius: 6,
                            border: '1px solid #f0f0f0',
                          }}
                        >
                          <div style={{ marginBottom: 8 }}>
                            <Text strong style={{ fontSize: 14 }}>{tier.tier}</Text>
                            <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                              ({tier.description})
                            </Text>
                          </div>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '8px 16px',
                          }}>
                            <div>
                              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Locations</Text>
                              <Text strong style={{ fontSize: 14 }}>{tier.locations}</Text>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Expense %</Text>
                              <Text strong style={{ fontSize: 14 }}>{tier.expensePct.toFixed(2)}%</Text>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                                {viewMode === 'weekly' ? 'Prior Wk Sales' : 'Qtr Sales'}
                              </Text>
                              <Text style={{ fontSize: 13 }}>{formatCurrency(tier.totalSales)}</Text>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                                {viewMode === 'weekly' ? 'Prior Wk GFS' : 'Qtr GFS'}
                              </Text>
                              <Text style={{ fontSize: 13 }}>{formatCurrency(tier.totalExpenses)}</Text>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Desktop: Table view
                    <Table
                      columns={[
                        {
                          title: 'Tier',
                          key: 'tier',
                          width: 200,
                          render: (_: any, record: any) => (
                            <span>
                              <Text strong>{record.tier}</Text>
                              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>({record.description})</Text>
                            </span>
                          ),
                        },
                        {
                          title: 'Locations',
                          dataIndex: 'locations',
                          key: 'locations',
                          width: 100,
                          align: 'right' as const,
                        },
                        {
                          title: viewMode === 'weekly' ? 'Prior Wk Sales' : 'Qtr Sales',
                          dataIndex: 'totalSales',
                          key: 'totalSales',
                          width: 140,
                          align: 'right' as const,
                          render: (val: number) => formatCurrency(val),
                        },
                        {
                          title: viewMode === 'weekly' ? 'Prior Wk GFS' : 'Qtr GFS',
                          dataIndex: 'totalExpenses',
                          key: 'totalExpenses',
                          width: 140,
                          align: 'right' as const,
                          render: (val: number) => formatCurrency(val),
                        },
                        {
                          title: 'GFS %',
                          dataIndex: 'expensePct',
                          key: 'expensePct',
                          width: 100,
                          align: 'right' as const,
                          render: (val: number) => `${val.toFixed(2)}%`,
                        },
                      ]}
                      dataSource={tierBreakdown}
                      pagination={false}
                      size="small"
                      bordered
                    />
                  )}
                </Card>
              );
            })()}

            {tierGroups.map((tierGroup, tierIndex) => (
              tierGroup.locations.length > 0 && (
                <div
                  key={tierGroup.tier}
                  ref={(el) => { tierCardRefs.current[tierGroup.tier] = el; }}
                  data-tour={tierIndex === 0 ? "expenses-table" : undefined}
                >
                  <Card
                    title={
                      <div style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        gap: isMobile ? 4 : 0,
                      }}>
                        <span>
                          <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>{tierGroup.tierLabel}</Text>
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: isMobile ? 12 : 14 }}>
                            ({tierGroup.tierDescription})
                          </Text>
                        </span>
                        <Space size={isMobile ? 8 : undefined}>
                          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
                            {tierGroup.locations.length} locations
                          </Text>
                          <Text type="secondary">|</Text>
                          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
                            Avg: {tierGroup.avgExpensePercentage.toFixed(2)}%
                          </Text>
                        </Space>
                      </div>
                    }
                    style={{ marginBottom: 16 }}
                  >
                    {isMobile ? (
                      <div>
                        {tierGroup.locations.map((locationExpense, idx) => (
                          <ExpenseMobileCard
                            key={idx}
                            locationExpense={locationExpense}
                            itemViewMode={itemViewMode}
                            setItemViewMode={setItemViewMode}
                            viewMode={viewMode}
                            getSalesForLocation={getSalesForLocation}
                            item8WeekAverages={item8WeekAverages[locationExpense.location]}
                            item4WeekAverages={item4WeekAverages[locationExpense.location]}
                            loadingAverages={loadingAverages[locationExpense.location]}
                            onExpand={(expanded) => {
                              if (expanded) {
                                handleLocationExpand(true, locationExpense);
                              }
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <Table
                        columns={columns}
                        dataSource={tierGroup.locations}
                        expandable={{
                          expandedRowRender,
                          expandedRowKeys: expandedRowKeys[tierGroup.tier] || [],
                          onExpand: (expanded, record) => {
                            handleLocationExpand(expanded, record);
                            setExpandedRowKeys(prev => ({
                              ...prev,
                              [tierGroup.tier]: expanded
                                ? [...(prev[tierGroup.tier] || []), record.location]
                                : (prev[tierGroup.tier] || []).filter(k => k !== record.location)
                            }));
                          },
                          expandIcon: ({ expanded, onExpand, record }) => (
                            <Button
                              type="link"
                              size="small"
                              onClick={(e) => onExpand(record, e)}
                              style={{
                                padding: '0 8px',
                                height: 24,
                                fontSize: 12,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              {expanded ? (
                                <>Hide <UpOutlined style={{ fontSize: 10 }} /></>
                              ) : (
                                <>View <DownOutlined style={{ fontSize: 10 }} /></>
                              )}
                            </Button>
                          ),
                        }}
                        rowKey="location"
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                        size="small"
                      />
                    )}
                  </Card>
                </div>
              )
            ))}
          </>
        )}

        {/* Compare Locations Modal */}
        <CompareLocationsModal
          open={isCompareModalOpen}
          onClose={() => setIsCompareModalOpen(false)}
          onCompare={(locationA, locationB) => {
            setComparedLocations([locationA, locationB]);
            setCompareMode(true);
          }}
          locations={expenseData}
          viewMode={viewMode}
          startDate={selectedDateRange?.startDate}
          endDate={selectedDateRange?.endDate}
        />
      </Space>
    </div>
  );
};

export default Expenses;
